/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { retry, concatMap } from 'rxjs/operators';
import { timer, Subscription, Observable } from 'rxjs';
import { UserService, Platform } from './user.service';
import { XdaStringsService } from './xda-strings.service';
import { RunningWorkflow } from './workflow.service';
import { FileService } from './file.service';
import { BasicAlertComponent } from '../components/alerts/basic-alert.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BrowserService } from './browser.service';
import { AutomatedJobHistory } from './automated-workflow.service';

@Injectable({
  providedIn: 'root',
})
export class WorkflowStatusService {
  baseUrl = 'api/workflow/';
  processingWorkflows: RunningWorkflow[] = [];
  jobsAutoDownloaded: string[] = [];
  statusSubscription: Subscription;
  trayStatusChange = new EventEmitter<TrayStatus>();
  workflowCanceled = new EventEmitter<string>();

  userLogoutSub: Subscription;
  userLoginSub: Subscription;
  accessListDiscoveredSub: Subscription;

  missedStatusSub: Subscription;

  // used for messaging
  completedStates = 0;
  processingStates = 0;
  erroredStates = 0;
  pendingDownloadStates = 0;

  alreadyPollingStatus = false;
  docRetentionDays: number;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private fileService: FileService,
    private xdaStringsService: XdaStringsService,
    private modalService: NgbModal,
    private browserService: BrowserService,
  ) {
    this.getMissedStatus();
    this.userLoginSub = this.userService.userAuthenticated.subscribe(() =>
      this.getMissedStatus(),
    );
    this.userLogoutSub = this.userService.userLoggedOff.subscribe(() =>
      this.stopAllStatusPolling(),
    );
    this.accessListDiscoveredSub = this.userService.discovered.subscribe(() =>
      this.getMissedStatus(),
    );
    this.docRetentionDays = this.xdaStringsService.strings['DOC_RETENTION_DAYS']
      ? parseInt(this.xdaStringsService.strings['DOC_RETENTION_DAYS'], 10)
      : 7;

    window.addEventListener('beforeunload', (e) => {
      if (this.userService.isGuestUser || this.fileService.hasUploadedFileId) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }

  getMissedStatus(): void {
    if (
      this.userService.isLoggedOn() &&
      this.userService.user?.isSubscriptionUser &&
      !this.userService.isGuestUser
    ) {
      this.missedStatusSub = this.http
        .get<RunningWorkflow[]>(`${this.baseUrl}MissedStatus`)
        .subscribe(
          (workflows: RunningWorkflow[]) => {
            workflows.forEach((mw) => {
              if (
                !this.processingWorkflows.find(
                  (x) => x.executionId === mw.executionId,
                )
              ) {
                this.processingWorkflows.push(mw);
              }
            });

            this.processingWorkflows.forEach((workflow) => {
              workflow.isMissedStatus = true;

              // Deal with existing workflows that use .png icons instead of .svg icons
              if (workflow.icon.image) {
                workflow.icon.image = workflow.icon.image.replace(
                  '.png',
                  '.svg',
                );
              }

              // Set output status
              workflow.outputStatus = [];
              workflow.status?.forEach((status) => {
                if (
                  status.serviceId == '00000000-0000-0000-0000-000000000000' &&
                  !status.status.startsWith('SDE_UPLOAD') &&
                  !status.status.startsWith('SDE_INPUT_FILE_DOES')
                ) {
                  // Must be an output
                  workflow.outputStatus?.push(status);
                }
              });

              // Start polling if every status is not in a terminal state
              if (
                !workflow.status?.every(
                  (s) =>
                    s.state == SqdStatusState.Completed ||
                    s.state == SqdStatusState.Errored ||
                    s.state == SqdStatusState.Canceled,
                )
              ) {
                this.startStatusRequests(workflow.id ?? '');
              }
            });

            const trayStatus = this.getTrayStatus();
            if (trayStatus.processingWorkflows > 0) {
              this.trayStatusChange.emit(trayStatus);
            }
          },
          (err) => {
            // Don't display this error message if we are navigating away
            if (this.userService.navigatingAway) {
              return;
            }
            const modalRef = this.modalService.open(BasicAlertComponent);
            modalRef.componentInstance.title = 'SDE_FAILED_GET_MISSED';
            console.log(err);
          },
        );
    }
  }

  getTrayStatus(workflowId?: string, statusState?: SqdStatusState): TrayStatus {
    const singleJob = this.isSingleJob();
    this.updateStateNumbers();
    const anyProcessing = this.processingStates > 0;
    const processingStates = this.processingStates;
    const erroredStates = this.erroredStates;
    const waitingStates = this.pendingDownloadStates;
    const completedStates = this.completedStates;
    const processingWorkflows = this.processingWorkflows.length;
    const infoState: InfoState = {
      workflowId: workflowId || '',
      statusState: statusState || SqdStatusState.Processing,
    };
    const result: TrayStatus = {
      infoState,
      singleJob,
      anyProcessing,
      processingWorkflows,
      processingStates,
      erroredStates,
      waitingStates,
      completedStates,
    };
    return result;
  }

  async getWorkflowPageCount(workflowId: string) {
    return await this.http
      .get<string>(`${this.baseUrl}PageCount/${workflowId}`)
      .toPromise();
  }

  async workflowErrorReport(workflowId: string) {
    const userId = this.userService.user?.id;
    const userEmail = this.userService.user?.email;

    return await this.http
      .get(`${this.baseUrl}WorkflowErrorReport/${userId}/${workflowId}`, {
        params: { userEmail: userEmail || '' },
      })
      .toPromise();
  }

  async autoWorkflowErrorReport(workflowId: string) {
    const userId = this.userService.user?.accessListId;
    const userEmail = this.userService.user?.email;
    return await this.http
      .get(`${this.baseUrl}WorkflowErrorReport/${userId}/${workflowId}`, {
        params: { userEmail: userEmail || '' },
      })
      .toPromise();
  }

  private updateStateNumbers(): void {
    // Count up all the completed, processing and errored states
    this.completedStates = 0;
    this.processingStates = 0;
    this.erroredStates = 0;
    this.pendingDownloadStates = 0;
    this.processingWorkflows.forEach((workflow) => {
      /// Ignore canceled
      if (!workflow.status?.some((x) => x.state == SqdStatusState.Canceled)) {
        // Count errors first
        if (workflow.status?.some((x) => x.state == SqdStatusState.Errored)) {
          this.erroredStates++;
          // then pending
        } else if (
          workflow.status?.some(
            (x) =>
              x.state == SqdStatusState.PendingHeld ||
              x.state == SqdStatusState.PendingDownload ||
              x.state == SqdStatusState.PendingPrint,
          )
        ) {
          this.pendingDownloadStates++;
          // check if completed
        } else if (
          workflow.status?.every((x) => x.state == SqdStatusState.Completed)
        ) {
          this.completedStates++;
        } else {
          // else
          this.processingStates++;
        }
      }
    });
  }

  isSingleJob(): boolean {
    return this.processingWorkflows && this.processingWorkflows.length == 1;
  }

  startProcessingWorkflow(workflow: RunningWorkflow): void {
    this.processingWorkflows.push(workflow);
  }

  updateWorkflowFileId(workflowId: string, inputFileId: string): void {
    const workflowToUpdate = this.processingWorkflows.find(
      (workflow) => workflow.id === workflowId,
    );
    if (workflowToUpdate) {
      workflowToUpdate.input.fileId = inputFileId;
    }
  }

  updateWorkflowId(prevWorkflowId: string, newWorkflowId: string): void {
    const workflowToUpdate = this.processingWorkflows.find(
      (workflow) => workflow.id === prevWorkflowId,
    );
    if (workflowToUpdate) {
      workflowToUpdate.id = newWorkflowId;
    }
  }

  updateWorkflowStatus(
    workflowId: string,
    statuses: SqdStatus[],
  ): RunningWorkflow | undefined {
    const workflowToUpdate = this.processingWorkflows.find(
      (workflow) => workflow.id === workflowId,
    );
    if (workflowToUpdate) {
      // Check to see if print has started
      const printingStartedStatus = workflowToUpdate.outputStatus?.find(
        (x) =>
          x.status === 'SDE_PRINTING' && x.state != SqdStatusState.PendingPrint,
      );
      workflowToUpdate.status = statuses;
      workflowToUpdate.outputStatus = [];

      statuses.forEach((status) => {
        if (status.serviceId && workflowToUpdate.services) {
          const serviceToUpdateNumber = workflowToUpdate.services.findIndex(
            (service) => service.name == status.serviceName,
          );
          // If Service to update is found
          if (serviceToUpdateNumber != -1) {
            // Update the service
            workflowToUpdate.services[serviceToUpdateNumber].status = [status];
          } else if (
            !status.status.startsWith('SDE_UPLOAD') &&
            !status.status.startsWith('SDE_INPUT_FILE_DOES') &&
            !status.status.startsWith('SDE_SCANNED_DOCUMENT_NO') &&
            !status.status.startsWith('SDE_WORKFLOW_CANCELED')
          ) {
            // Must be an output, but if its print, and the print has started, dont update the status based on server
            if (status.status == 'SDE_PRINTING' && printingStartedStatus) {
              workflowToUpdate.outputStatus?.push(printingStartedStatus);
            } else {
              workflowToUpdate.outputStatus?.push(status);
            }
          }

          // If status indicated canceled, update other services
          if (status.state == SqdStatusState.Canceled) {
            this.updateProcessingServicesWithState(
              workflowToUpdate,
              status.state,
            );
          }
        }
      });

      // If something errored, error the status of all processing services
      if (statuses.some((x) => x.state == SqdStatusState.Errored)) {
        this.updateProcessingServicesWithState(
          workflowToUpdate,
          SqdStatusState.Errored,
        );
      }

      this.trayStatusChange.emit(this.getTrayStatus(workflowId));
      return workflowToUpdate;
    }
    return undefined;
  }

  updateWorkflowPrintStatus(workflowId: string, status: SqdStatus): void {
    const workflowToUpdate = this.processingWorkflows.find(
      (workflow) => workflow.id === workflowId,
    );
    if (workflowToUpdate) {
      const printOutputStatus = workflowToUpdate.outputStatus?.find(
        (x) => x.status === 'SDE_PRINTING',
      );
      const printStatus = workflowToUpdate.status?.find(
        (x) => x.status === 'SDE_PRINTING',
      );
      if (printStatus) {
        printStatus.state = status.state;
      }
      if (printOutputStatus) {
        printOutputStatus.state = status.state;
        printOutputStatus.status = status.status;
        printOutputStatus.statusRaw = status.statusRaw;
      } else {
        workflowToUpdate.outputStatus?.push(status);
      }

      // If any services were left in processing state, move them to completed
      this.updateProcessingServicesWithState(
        workflowToUpdate,
        SqdStatusState.Completed,
      );

      if (
        status.state == SqdStatusState.Canceled ||
        status.state == SqdStatusState.Errored
      ) {
        this.updateProcessingServicesWithState(workflowToUpdate, status.state);
      }

      this.trayStatusChange.emit(this.getTrayStatus(workflowId, status.state));
    }
  }

  updateProcessingServicesWithState(
    workflowToUpdate: RunningWorkflow,
    statusState: SqdStatusState,
  ) {
    workflowToUpdate.services?.forEach((service) => {
      if (service.status) {
        service.status.forEach((s) => {
          if (s && s.state == SqdStatusState.Processing) {
            s.state = statusState;
          }
        });
      }
    });

    // Remove pause if job is paused
    const pauseIndex = workflowToUpdate.outputStatus?.findIndex(
      (x) => x.status == 'SDE_PAUSED',
    );
    if (pauseIndex != -1 && typeof pauseIndex !== 'undefined') {
      workflowToUpdate.outputStatus?.splice(pauseIndex, 1);
    }
  }

  errorReportWorkflow(
    workflow: RunningWorkflow | AutomatedJobHistory,
    isAutoWorkflow = false,
  ): void {
    if (!this.userService.isGuestUser) {
      const modalRef = this.modalService.open(BasicAlertComponent, {
        windowClass: 'xrx-alert-window',
      });
      modalRef.componentInstance.title = 'SDE_SEND_DIAGNOSTIC_INFORMATION1';
      modalRef.componentInstance.buttonText = 'SDE_NO';
      modalRef.componentInstance.okButtonText = 'SDE_YES';
      modalRef.result.then((data) => {
        if (data === 'Ok') {
          if (isAutoWorkflow) {
            this.autoWorkflowErrorReport(workflow.id || '');
          } else {
            this.workflowErrorReport(workflow.id || '');
          }
        }
      });
    }
  }

  cancelWorkflow(workflow: RunningWorkflow): void {
    let title = 'SDE_CONFIRM_CANCEL_JOB',
      alertBodyText = 'SDE_MAY_STILL_INCUR',
      buttonText = 'SDE_NO',
      okButtonText = 'SDE_YES';

    if (this.userService.isGuestUser) {
      alertBodyText = 'SDE_WILL_NOT_BE7';
      buttonText = 'SDE_CONTINUE_MY_JOB';
      okButtonText = 'SDE_CANCEL_AND_EXIT';
    } else if (
      workflow.services &&
      workflow.services.some((s) => s.name === 'SDE_SEND_FAX')
    ) {
      title = 'SDE_ATTEMPTING_CANCEL_FAX';
      alertBodyText = 'SDE_MAY_STILL_INCUR';
      buttonText = 'SDE_CONTINUE_MY_JOB';
      okButtonText = 'SDE_CANCEL_AND_EXIT';
    }

    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.body = alertBodyText;
    modalRef.componentInstance.buttonText = buttonText;
    modalRef.componentInstance.okButtonText = okButtonText;
    modalRef.result.then((data) => {
      if (data === 'Ok') {
        this.okToCancelWorkflow(workflow);
      }
    });
  }

  okToCancelWorkflow(workflow: RunningWorkflow) {
    const canceledStatusText = this.userService.isGuestUser
      ? 'SDE_JOB_CANCELED'
      : 'SDE_WORKFLOW_CANCELED';
    const userId = this.userService.user?.id;
    // if no workflow specified, cancel first/only workflow processing
    if (!workflow.id) {
      workflow.id = this.processingWorkflows[0].id;
    }

    if (!workflow.executionId) {
      workflow.executionId = this.processingWorkflows[0].executionId;
    }

    this.workflowCanceled.emit(workflow.executionId);

    // Temp workflow id's are not guids which means we still uploading, so just cancel the upload
    if (!workflow.id || workflow.id.length < 36) {
      const canceledStatus: SqdStatus = {
        state: SqdStatusState.Canceled,
        status: canceledStatusText,
        statusRaw: canceledStatusText,
      };
      this.updateWorkflowStatus(workflow.id || '', [canceledStatus]);
    } else {
      this.http
        .put<boolean>(`${this.baseUrl}Cancel/${userId}/${workflow.id}`, null)
        .subscribe(
          (canceled: boolean) => {
            if (canceled) {
              const runningWorkflow = this.processingWorkflows.find(
                (wf) => wf.id === workflow.id,
              );
              const canceledStatus: SqdStatus = {
                state: SqdStatusState.Canceled,
                status: canceledStatusText,
                statusRaw: canceledStatusText,
              };
              runningWorkflow?.status?.push(canceledStatus);
              if (typeof runningWorkflow !== 'undefined') {
                this.updateProcessingServicesWithState(
                  runningWorkflow,
                  SqdStatusState.Canceled,
                );
              }

              // If guest user, after a cancel, exit
              if (this.userService.isGuestUser) {
                this.guestLogoutAndClose();
              }
            } else {
              const modalRef = this.modalService.open(BasicAlertComponent);
              modalRef.componentInstance.title = 'SDE_UNABLE_CANCEL_WORKFLOW';
            }
          },
          (err) => {
            console.log(err);
            const modalRef = this.modalService.open(BasicAlertComponent);
            modalRef.componentInstance.title = 'SDE_FAILED_CANCEL_WORKFLOW';
          },
        );
    }
  }

  removeWorkflow(workflowId?: string): void {
    if (!workflowId) {
      this.processingWorkflows = [];
      this.trayStatusChange.emit(this.getTrayStatus());
    } else {
      const workflowIndex = this.processingWorkflows.findIndex(
        (workflow) => workflow.id === workflowId,
      );
      if (workflowIndex > -1) {
        const removedWorkflow = this.processingWorkflows.splice(
          workflowIndex,
          1,
        )[0];
        // If we removed a pending download job, make sure to notify server that we "got" the file
        if (
          removedWorkflow.status?.some(
            (s) => s.state == SqdStatusState.PendingDownload,
          )
        ) {
          this.notifyFinishedWorkflow(workflowId);
        }
        if (
          removedWorkflow.status?.some(
            (s) => s.state == SqdStatusState.PendingPrint,
          )
        ) {
          this.notifyFinishedPrintWorkflow(workflowId);
        }
        if (this.isSingleJob()) {
          const onlyWorkflow = this.processingWorkflows[0];

          onlyWorkflow.status?.forEach((s) => {
            this.trayStatusChange.emit(
              this.getTrayStatus(onlyWorkflow.id || '', s.state),
            );
          });
        } else {
          this.trayStatusChange.emit(this.getTrayStatus());
        }
      }
    }
  }

  startStatusRequests(jobId: string): void {
    if (!this.alreadyPollingStatus) {
      this.alreadyPollingStatus = true;
      this.statusSubscription = this.startPollingStatus()
        .pipe(retry(3))
        .subscribe(
          (jobs: Record<string, SqdStatus[]>) => {
            const jobIds = Object.keys(jobs);
            for (const jobId of jobIds) {
              const statuses = jobs[jobId];
              if (statuses) {
                const updatedWorkflow = this.updateWorkflowStatus(
                  jobId,
                  statuses,
                );

                if (updatedWorkflow) {
                  if (
                    !updatedWorkflow.isMissedStatus &&
                    updatedWorkflow.outputStatus?.some(
                      (x) =>
                        x.state == SqdStatusState.PendingDownload ||
                        x.state == SqdStatusState.PendingPrint,
                    )
                  ) {
                    // Only start attempting to download the file once
                    if (!this.jobsAutoDownloaded.includes(jobId)) {
                      const finishedWorkflow = this.processingWorkflows.find(
                        (workflow) => workflow.id === jobId,
                      );
                      if (finishedWorkflow) {
                        this.downloadAndFinish(finishedWorkflow);
                      }
                      this.jobsAutoDownloaded.push(jobId);
                    }
                  }
                }
              }
            }

            // filter out the jobs that failed during uploadFile phase, as their job id is not a Guid.
            const missingJobs = this.processingWorkflows.filter(
              (x) =>
                !jobs[x.id ?? ''] &&
                x.id?.length == 36 &&
                !this.workflowIsComplete(x),
            );
            missingJobs.forEach((missingJob) => {
              this.statusSingleWorkflow(missingJob);
            });

            if (this.userService.isGuestUser) {
              if (missingJobs.length == 0) {
                this.stopPollingStatus();
                const finishedWorkflow = this.processingWorkflows[0];
                if (
                  finishedWorkflow.outputStatus?.some(
                    (x) => x.state == SqdStatusState.PendingDownload,
                  )
                ) {
                  this.downloadAndFinish(finishedWorkflow);
                }
              }
            } else {
              if (jobIds.length == 0) {
                this.stopPollingStatus();
              }
            }
          },
          () => {
            const errorStatus: SqdStatus = {
              state: SqdStatusState.Errored,
              status: this.xdaStringsService.strings['SDE_FAILED_GET_WORKFLOW'],
              statusRaw:
                this.xdaStringsService.strings['SDE_FAILED_GET_WORKFLOW'],
            };
            this.updateWorkflowStatus(jobId, [errorStatus]);
          },
        );
    }
  }

  statusSingleWorkflow(runningWorkflow: RunningWorkflow): void {
    const jobId = runningWorkflow.id;
    this.http.get<SqdStatus[]>(`${this.baseUrl}Status/${jobId}`).subscribe(
      (workflowStatus: SqdStatus[]) => {
        if (workflowStatus) {
          this.updateWorkflowStatus(jobId ?? '', workflowStatus);
        }
      },
      (e) => {
        console.log(e);
      },
    );
  }

  workflowIsUploading(runningWorkflow: RunningWorkflow): boolean {
    // assume uploading until we prove it is not
    let isUploading = true;
    const uploadingStatus = runningWorkflow.status?.find((x) =>
      x.status.startsWith('SDE_UPLOAD'),
    );

    if (uploadingStatus) {
      isUploading = uploadingStatus.state == SqdStatusState.Processing;
    }

    return isUploading;
  }

  workflowIsComplete(runningWorkflow: RunningWorkflow): boolean {
    // assume complete until we prove it is not
    const isComplete = true;

    const canceledStatus = runningWorkflow.status?.find(
      (x) => x.status === 'SDE_WORKFLOW_CANCELED',
    );
    if (canceledStatus) {
      // return right away
      return true;
    }

    const downloadEnabled = runningWorkflow.output.outputOptions?.some(
      (o) => o.value == 'Download',
    );
    if (downloadEnabled) {
      const downloadStatus = runningWorkflow.outputStatus?.find(
        (x) => x.status === 'SDE_DOWNLOADING_FILE',
      );
      if (!downloadStatus) {
        // return right away
        return false;
      }
    }

    const emailEnabled = runningWorkflow.output.outputOptions?.some(
      (o) => o.value == 'Email',
    );
    if (emailEnabled) {
      const emailStatus = runningWorkflow.outputStatus?.find(
        (x) => x.status === 'SDE_SENDING_EMAIL1',
      );
      if (!emailStatus) {
        // return right away
        return false;
      }
    }

    const cloudEnabled = runningWorkflow.output.outputOptions?.some(
      (o) => o.value == 'Cloud',
    );
    if (cloudEnabled) {
      const cloudStatus = runningWorkflow.outputStatus?.find(
        (x) => x.status === 'SDE_SENDING_CLOUD',
      );
      if (!cloudStatus) {
        // return right away
        return false;
      }
    }

    const printEnabled = runningWorkflow.output.outputOptions?.some(
      (o) => o.value == 'Print',
    );
    if (printEnabled) {
      const printStatus = runningWorkflow.outputStatus?.find(
        (x) => x.status === 'SDE_PRINTING',
      );
      if (!printStatus) {
        // return right away
        return false;
      }
    }

    return isComplete;
  }

  guestLogoutAndClose() {
    this.removeWorkflow();
    this.userService.isGuestUser = false;
    this.userService.logOff();
  }

  // return observable that will poll every 5 seconds until subscription ends
  private startPollingStatus(): Observable<Record<string, SqdStatus[]>> {
    return timer(0, 5000).pipe(
      concatMap(() =>
        this.http.get<Record<string, SqdStatus[]>>(`${this.baseUrl}Status`),
      ),
    );
  }

  private stopPollingStatus(): void {
    // Delete subscription
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
      this.alreadyPollingStatus = false;
    }
  }

  private stopAllStatusPolling(): void {
    // Delete status subscription
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
      this.alreadyPollingStatus = false;
    }
    // Empty processing workflows
    this.processingWorkflows = [];
    // Inform status tray
    this.trayStatusChange.emit(this.getTrayStatus());

    if (this.missedStatusSub) {
      this.missedStatusSub.unsubscribe();
    }
  }

  public async downloadInterim(workflow: RunningWorkflow): Promise<void> {
    if (this.isPassedRetentionDays(workflow)) {
      return this.okToCancelWorkflow(workflow);
    }

    const id = workflow.input.fileId;
    const lastCompletedServiceId = workflow.services
      ?.slice()
      ?.reverse()
      ?.find(
        (x) => x.status && x.status[0].state == SqdStatusState.Completed,
      )?.id;

    let fileName = workflow.output.file.name;

    if (this.browserService.isMobile) {
      const url = this.fileService.getInterimDownloadFileUrl(
        id,
        lastCompletedServiceId ?? '',
      );
      this.fileService
        .validateMobileDownload(id, lastCompletedServiceId ?? '')
        .subscribe(
          () => {
            this.makeAndClickLink(url, fileName);
          },
          () => {
            const modalRef = this.modalService.open(BasicAlertComponent);
            modalRef.componentInstance.title = 'SDE_DOWNLOAD_FAILED1';
          },
        );
      return;
    }

    this.fileService
      .downloadInterimFile(id, lastCompletedServiceId ?? '')
      .subscribe(
        (resp) => {
          const contentDisposition = resp.headers.get('content-disposition');
          fileName = this.getFileName(contentDisposition ?? '', fileName);
          if (resp.body) {
            this.saveFileLocally(resp.body, fileName);
          }
        },
        () => {
          const modalRef = this.modalService.open(BasicAlertComponent);
          modalRef.componentInstance.title = 'SDE_DOWNLOAD_FAILED1';
        },
      );
  }

  public async continueWorkflow(workflow: RunningWorkflow): Promise<void> {
    if (this.isPassedRetentionDays(workflow)) {
      return this.okToCancelWorkflow(workflow);
    }

    this.http.get(`${this.baseUrl}ContinueWorkflow/${workflow.id}`).subscribe();

    // Wait 5 seconds and start polling (in case polling had stopped);
    setTimeout(async () => {
      this.startStatusRequests(workflow.id ?? '');
    }, 5000);
  }

  public isPassedRetentionDays(workflow: RunningWorkflow) {
    const initialDate =
      typeof workflow.initiatedAt === 'string'
        ? Date.parse(workflow.initiatedAt)
        : (workflow.initiatedAt?.getTime() ?? 0);
    const datesPassed =
      (new Date().getTime() - initialDate) / (1000 * 60 * 60 * 24);
    return datesPassed > this.docRetentionDays;
  }

  public async downloadAndFinish(workflow: RunningWorkflow): Promise<void> {
    const id = workflow.input.fileId;
    let fileName = workflow.output.file.name;
    if (
      this.userService.platform === Platform.WEBPORTAL &&
      workflow.output.outputOptions?.some((o) => o.value == 'Download')
    ) {
      // If download is not pending don't start downloading
      const downloadStatus = workflow.outputStatus?.find(
        (x) => x.status === 'SDE_DOWNLOADING_FILE',
      );
      if (downloadStatus?.state != SqdStatusState.PendingDownload) {
        return;
      }

      if (this.browserService.isMobile) {
        return this.downloadFileOnMobileDevice(workflow);
      }

      // Only let a user download, if they are not on a device
      this.fileService.downloadFile(id, workflow.id ?? '').subscribe(
        (resp) => {
          const contentDisposition = resp.headers.get('content-disposition');
          fileName = this.getFileName(contentDisposition ?? '', fileName);
          if (resp.body) {
            this.saveFileLocally(resp.body, fileName);
          }

          if (workflow.outputStatus) {
            const downloadStatus = workflow.outputStatus.find(
              (s) => s.status === 'SDE_DOWNLOADING_FILE',
            );
            if (downloadStatus) {
              downloadStatus.state = SqdStatusState.Completed;
              // Download completed successfully, remove from list
              const autoDownloadJobIdIndex = this.jobsAutoDownloaded.indexOf(
                workflow.id ?? '',
              );
              if (autoDownloadJobIdIndex != -1) {
                this.jobsAutoDownloaded.splice(autoDownloadJobIdIndex, 1);
              }
            }
          }
        },
        () => {
          const workflowName =
            this.xdaStringsService.strings[workflow.name] || workflow.name;
          const errorStatus: SqdStatus = {
            state: SqdStatusState.Errored,
            status: 'SDE_DOWNLOADING_FILE',
            statusRaw: this.xdaStringsService.strings[
              'SDE_FMTSTR_FAILED_DOWNLOAD'
            ].replace('{0}', workflowName),
            serviceId: '00000000-0000-0000-0000-000000000000',
          };
          this.updateWorkflowStatus(workflow.id ?? '', [errorStatus]);
        },
      );
    }
  }

  private getFileName(
    contentDisposition: string,
    localFileName: string,
  ): string {
    let fileName = localFileName;
    if (contentDisposition) {
      const arr = contentDisposition.split(';');
      const utfFileNameEntry = arr.filter((x) => x.includes('filename*='));
      const asciiifileNameEntry = arr.filter((x) => x.includes('filename='));
      const fileNameEntry = utfFileNameEntry ?? asciiifileNameEntry;

      if (fileNameEntry && fileNameEntry.length) {
        const namePair = fileNameEntry[0].split('=');
        if (namePair && namePair.length > 1) {
          fileName = namePair[1].trim().replace(/(^")|(^UTF-8'')|("$)/g, '');
          fileName = decodeURIComponent(fileName);
        }
      }
    }
    return fileName;
  }

  private notifyFinishedWorkflow(workflowId: string): void {
    const userId = this.userService.user?.id;
    this.http
      .put(`${this.baseUrl}Complete/${userId}/${workflowId}`, null)
      .subscribe(
        () => {
          // empty
        },
        () => {
          if (!this.browserService.isMobile) {
            const modalRef = this.modalService.open(BasicAlertComponent);
            modalRef.componentInstance.title = 'SDE_FAILED_COMPLETE_WORKFLOW';
          }
        },
      );
  }
  private notifyFinishedPrintWorkflow(workflowId: string): void {
    const userId = this.userService.user?.id;
    this.http
      .put(`${this.baseUrl}CompletePrint/${userId}/${workflowId}`, null)
      .subscribe(
        () => {
          // empty
        },
        () => {
          if (!this.browserService.isMobile) {
            const modalRef = this.modalService.open(BasicAlertComponent);
            modalRef.componentInstance.title = 'SDE_FAILED_COMPLETE_WORKFLOW';
          }
        },
      );
  }

  private saveFileLocally(blob: Blob, fileName: string): void {
    const nav = navigator as Navigator & {
      msSaveOrOpenBlob?: (blob: Blob, fileName: string) => void;
    };
    if (nav.msSaveOrOpenBlob) {
      nav.msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement(
        'a',
      ) as HTMLAnchorElement;

      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }
  }

  private downloadFileOnMobileDevice(workflow: RunningWorkflow): void {
    const url = this.fileService.getDownloadFileUrl(
      workflow.input.fileId,
      workflow.id ?? '',
      this.userService.user?.id ?? '',
    );

    this.fileService.validateMobileDownload(workflow.input.fileId).subscribe(
      () => {
        this.makeAndClickLink(url, workflow.output.file.name);
        if (workflow.outputStatus) {
          const downloadStatus = workflow.outputStatus.find(
            (s) => s.status === 'SDE_DOWNLOADING_FILE',
          );
          if (downloadStatus) {
            downloadStatus.state = SqdStatusState.Completed;
          }
        }
      },
      () => {
        const workflowName =
          this.xdaStringsService.strings[workflow.name] || workflow.name;
        const errorStatus: SqdStatus = {
          state: SqdStatusState.Errored,
          status: 'SDE_DOWNLOADING_FILE',
          statusRaw: this.xdaStringsService.strings[
            'SDE_FMTSTR_FAILED_DOWNLOAD'
          ].replace('{0}', workflowName),
          serviceId: '00000000-0000-0000-0000-000000000000',
        };
        this.updateWorkflowStatus(workflow.id ?? '', [errorStatus]);
      },
    );
  }

  private makeAndClickLink(url: string, fileName: string) {
    const a: HTMLAnchorElement = document.createElement(
      'a',
    ) as HTMLAnchorElement;
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

export interface TrayStatus {
  infoState?: InfoState;
  singleJob: boolean;
  anyProcessing: boolean;
  processingWorkflows: number;
  processingStates: number;
  erroredStates: number;
  waitingStates: number;
  completedStates: number;
}

export interface SqdStatus {
  state: SqdStatusState;
  status: string;
  statusRaw: string;
  serviceId?: string;
  serviceName?: string;
}

export enum SqdStatusState {
  Pending,
  PendingHeld,
  PendingDownload,
  Processing,
  Canceled,
  Errored,
  Completed,
  PendingPrint,
}

export interface InfoState {
  workflowId: string;
  statusState: SqdStatusState;
}

export interface MFDJobStatus {
  jobId: string;
  jobState: MFDJobState;
  jobStateReasons: string;
}

export enum MFDJobState {
  Pending = 'Pending',
  PendingHeld = 'PendingHeld',
  Processing = 'Processing',
  Paused = 'Paused',
  ProcessingStopped = 'ProcessingStopped',
  Canceled = 'Canceled',
  Aborted = 'Aborted',
  Completed = 'Completed',
}
